import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Grid, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import {
  COLOR_CX,
  DRIVER_APP_FULFILMENT_DONUT_HEIGHT,
  FAILURE_REASON_COLORS,
  FAILURE_REASON_INFO,
  FAILURE_REASONS_CARD_INFO,
} from 'components/reports/Metrics/constants/DriverAppFulfilment'
import { DriverAppFulfilmentReasonsDto } from 'generated/graphql'

import { FailureReasonsChart } from './FailureReasonsChart'

interface Props {
  data?: DriverAppFulfilmentReasonsDto | null
  loading?: boolean
}

export const FailureReasonsCard = ({ data, loading }: Props) => {
  const reasons = data?.reasons ?? []
  const totalJobs = data?.cxFulfilledJobs ?? 0

  return (
    <Paper
      variant='outlined'
      sx={{ p: 2.5, height: '100%', borderRadius: 2, borderTop: `3px solid ${COLOR_CX}` }}>
      <Box mb={2}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle1' fontWeight={700}>
            Why jobs were not fulfilled through Driver App
          </Typography>
          <Tooltip title={FAILURE_REASONS_CARD_INFO}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>
        <Typography variant='body2' color='primary.main'>
          Breakdown of jobs fulfilled via CX.
        </Typography>
      </Box>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={DRIVER_APP_FULFILMENT_DONUT_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <Grid container spacing={3} alignItems='center'>
          <Grid item xs={12} sm={5}>
            <FailureReasonsChart reasons={reasons} totalJobs={totalJobs} />
          </Grid>

          <Grid item xs={12} sm={7}>
            <Stack spacing={1.5}>
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <Box sx={{ width: 10, flexShrink: 0 }} />
                <Box sx={{ flexGrow: 1 }} />
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ minWidth: 56, textAlign: 'right' }}>
                  Jobs
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ minWidth: 72, textAlign: 'right' }}>
                  % of failed
                </Typography>
              </Stack>

              {reasons.map((reason) => (
                <Stack key={reason.reason} direction='row' spacing={1.5} alignItems='center'>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: FAILURE_REASON_COLORS[reason.reason],
                    }}
                  />
                  <Stack direction='row' spacing={1} alignItems='center' sx={{ flexGrow: 1 }}>
                    <Typography variant='body2' fontWeight={600}>
                      {reason.label}
                    </Typography>
                    <Tooltip title={FAILURE_REASON_INFO[reason.reason]}>
                      <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
                    </Tooltip>
                  </Stack>
                  <Typography
                    variant='body2'
                    fontWeight={700}
                    whiteSpace='nowrap'
                    sx={{ minWidth: 56, textAlign: 'right' }}>
                    {reason.jobCount.toLocaleString()}
                  </Typography>
                  <Typography
                    variant='body2'
                    fontWeight={700}
                    whiteSpace='nowrap'
                    sx={{ minWidth: 72, textAlign: 'right' }}>
                    {Number(reason.percentage).toFixed(1)}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      )}
    </Paper>
  )
}
